<template>
<div>
    <!-- Hero -->
    <section class="pt-12 pt-md-14 pb-8">
        <div class="container">
            <div class="row justify-content-center text-center">
                <div class="col-12 col-md-10 col-lg-8">
                    <h1 class="display-3 fw-bold">Instagram & TikTok Data Platform</h1>
                    <p class="fs-lg text-muted mb-3">
                        Three ways to access social media data at scale: fast Cache API for real-time access,
                        ready-made Datasets for bulk analytics, and MCP Server for AI assistants.
                    </p>
                    <p class="text-muted mb-6">
                        No proxies to manage. No accounts to maintain. No rate limits to worry about.
                    </p>
                    <div class="d-flex justify-content-center gap-3">
                        <router-link to="/registration" class="btn btn-primary shadow lift">
                            Try Free <i class="fe fe-arrow-right ms-2"></i>
                        </router-link>
                        <router-link to="/docs" class="btn btn-outline-primary lift">View Docs</router-link>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Products -->
    <section class="py-8 py-md-10 products-section">
        <div class="container">
            <div class="row justify-content-center text-center mb-6">
                <div class="col-12 col-md-8">
                    <h2 class="fw-bold">Three products, one API key</h2>
                    <p class="text-muted">Pick the product that fits your workflow — or use all three.</p>
                </div>
            </div>
            <div class="row">
                <div class="col-12 col-lg-4 mb-4" v-for="p in products" :key="p.title">
                    <router-link :to="p.link" class="card product-card h-100 text-decoration-none text-reset card-hover border-0">
                        <div class="card-body p-5">
                            <div class="display-5 mb-3">{{ p.emoji }}</div>
                            <h3 class="mb-2">{{ p.title }}</h3>
                            <p class="text-muted mb-4">{{ p.tagline }}</p>
                            <ul class="list-unstyled text-muted mb-4">
                                <li class="mb-2" v-for="f in p.features" :key="f">
                                    <i class="fe fe-check text-success me-2"></i>{{ f }}
                                </li>
                            </ul>
                            <span class="fw-bold text-primary">
                                Learn more <i class="fe fe-arrow-right ms-1"></i>
                            </span>
                        </div>
                    </router-link>
                </div>
            </div>
        </div>
    </section>

    <!-- Why DataLikers -->
    <section class="py-8 py-md-10">
        <div class="container">
            <div class="row justify-content-center text-center mb-6">
                <div class="col-12 col-md-8">
                    <h2 class="fw-bold">Why teams choose {{ $state.siteName }}</h2>
                    <p class="text-muted">
                        Building with social media data is hard. Direct scraping gets blocked,
                        official APIs are closed for most use cases, and maintaining infrastructure is expensive.
                        {{ $state.siteName }} solves all of this.
                    </p>
                </div>
            </div>
            <div class="row">
                <div class="col-12 col-md-6 mb-5" v-for="r in reasons" :key="r.title">
                    <div class="d-flex">
                        <div class="me-4">
                            <div class="badge bg-primary-soft text-primary rounded-circle p-3">
                                <i :class="['fe', r.icon, 'fs-4']"></i>
                            </div>
                        </div>
                        <div>
                            <h3 class="fs-5">{{ r.title }}</h3>
                            <p class="text-muted mb-0" v-html="r.body"></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Data Available -->
    <section class="py-8 py-md-10 data-section">
        <div class="container">
            <div class="row justify-content-center text-center mb-6">
                <div class="col-12 col-md-8">
                    <h2 class="fw-bold">What data is available</h2>
                    <p class="text-muted">Public data across Instagram and TikTok</p>
                </div>
            </div>
            <div class="row">
                <div class="col-12 col-md-6 col-lg-4 mb-4" v-for="cat in categories" :key="cat.title">
                    <div class="card data-card h-100 border-0">
                        <div class="card-body">
                            <div class="d-flex align-items-center mb-3">
                                <span class="fs-3 me-3">{{ cat.icon }}</span>
                                <h3 class="mb-0 fs-4">{{ cat.title }}</h3>
                            </div>
                            <ul class="list-unstyled text-muted mb-0">
                                <li class="mb-1" v-for="f in cat.features" :key="f">
                                    <i class="fe fe-check text-success me-2"></i>{{ f }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4 mb-4">
                    <div class="card h-100 bg-primary text-white border-0">
                        <div class="card-body d-flex flex-column justify-content-center text-center">
                            <div class="display-4 mb-3">🚀</div>
                            <h3 class="text-white mb-2">Try Free</h3>
                            <p class="text-white-80 mb-4">100 requests free. No credit card required.</p>
                            <router-link to="/registration" class="btn btn-white shadow lift">
                                Create Account <i class="fe fe-arrow-right ms-2"></i>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Top Endpoints -->
    <section class="py-8 py-md-10">
        <div class="container">
            <div class="row justify-content-center text-center mb-8">
                <div class="col-12 col-md-8">
                    <h2 class="fw-bold">Most popular endpoints</h2>
                    <p class="text-muted">See how developers use {{ $state.siteName }} in real-world scenarios</p>
                </div>
            </div>

            <div
                v-for="(ep, idx) in endpoints"
                :key="ep.path"
                class="row align-items-center mb-8"
                :class="{ 'flex-md-row-reverse': idx % 2 === 1 }"
            >
                <div class="col-12 col-md-6 mb-4 mb-md-0">
                    <div class="card code-card rounded-lg border-0">
                        <div class="card-body p-4">
                            <pre class="text-white mb-0" v-html="ep.code" style="font-size: 0.82rem; white-space: pre-wrap;"></pre>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6">
                    <span class="badge bg-success-soft text-success mb-2">GET</span>
                    <span class="text-muted">{{ ep.path }}</span>
                    <h3 class="mt-2">{{ ep.title }}</h3>
                    <p class="text-muted">{{ ep.desc }}</p>
                    <router-link to="/docs" class="fw-bold text-decoration-none">View in docs <i class="fe fe-arrow-right ms-1"></i></router-link>
                </div>
            </div>
        </div>
    </section>

    <!-- Code Examples -->
    <section class="py-8 py-md-10 code-examples-section">
        <div class="container">
            <div class="row justify-content-center text-center mb-6">
                <div class="col-12 col-md-8">
                    <h2 class="fw-bold">Simple integration</h2>
                    <p class="text-muted">One API key, one HTTP request. Works with any language that can speak HTTP.</p>
                </div>
            </div>
            <div class="row justify-content-center">
                <div class="col-12 col-lg-10">
                    <CodeExamples />
                </div>
            </div>
        </div>
    </section>

    <!-- Use Cases -->
    <section class="py-8 py-md-10">
        <div class="container">
            <div class="row justify-content-center text-center mb-6">
                <div class="col-12 col-md-8">
                    <h2 class="fw-bold">What you can build with {{ $state.siteName }}</h2>
                </div>
            </div>
            <div class="row">
                <div class="col-12 col-md-6 col-lg-3 mb-4 text-center" v-for="u in useCases" :key="u.title">
                    <div class="fs-1 mb-3">{{ u.emoji }}</div>
                    <h4>{{ u.title }}</h4>
                    <p class="text-muted small">{{ u.body }}</p>
                </div>
            </div>
        </div>
    </section>

    <!-- Bottom CTA -->
    <section class="py-8 py-md-10 bg-primary">
        <div class="container text-center">
            <h2 class="fw-bold text-white mb-3">Ready to get started?</h2>
            <p class="text-white-80 mb-6">100 free API requests to try. No credit card required.</p>
            <div class="d-flex justify-content-center gap-3">
                <router-link to="/registration" class="btn btn-white shadow lift">
                    Create Free Account
                </router-link>
                <router-link to="/pricing" class="btn btn-outline-white lift text-white">
                    View Pricing
                </router-link>
            </div>
        </div>
    </section>
</div>
</template>

<script>
import CodeExamples from './CodeExamples.vue'

export default {
    components: { CodeExamples },
    computed: {
        apiBase() {
            return `${this.$state.siteProto}://api.${this.$state.siteDomain}`
        },
        endpoints() {
            const base = this.apiBase
            return [
                {
                    path: '/v1/user/by/username',
                    title: 'User profiles',
                    desc: 'Get any public Instagram profile with a single request — PK, full name, biography, follower/following counts, media count, and profile picture URL. Use it for influencer research, lead generation, or CRM enrichment.',
                    code: `<span class="text-muted"># Get Instagram user profile</span>
curl <span class="text-warning">"${base}/v1/user/by/username?username=instagram"</span> \\
  -H <span class="text-warning">"x-access-key: YOUR_ACCESS_KEY"</span>

<span class="text-muted"># Response</span>
{ <span class="text-primary">"pk"</span>: 25025320,
  <span class="text-primary">"username"</span>: <span class="text-warning">"instagram"</span>,
  <span class="text-primary">"follower_count"</span>: 684042497,
  <span class="text-primary">"media_count"</span>: 7891,
  <span class="text-primary">"is_verified"</span>: true,
  <span class="text-primary">"updated_at"</span>: <span class="text-warning">"2026-04-18T10:15:00Z"</span> }`
                },
                {
                    path: '/v1/user/about',
                    title: 'Extended user metadata',
                    desc: 'Go beyond the standard profile — retrieve country, account registration date, former usernames, and other metadata not available in the main endpoint. Essential for account vetting and historical analysis.',
                    code: `<span class="text-muted"># Get extended user info</span>
curl <span class="text-warning">"${base}/v1/user/about?id=25025320"</span> \\
  -H <span class="text-warning">"x-access-key: YOUR_ACCESS_KEY"</span>

<span class="text-muted"># Response</span>
{ <span class="text-primary">"country"</span>: <span class="text-warning">"United States"</span>,
  <span class="text-primary">"date_joined"</span>: <span class="text-warning">"2010-10-06"</span>,
  <span class="text-primary">"former_usernames"</span>: [
    <span class="text-warning">"instagramapp"</span>
  ] }`
                },
                {
                    path: '/v1/user/face',
                    title: 'AI face analysis',
                    desc: 'A unique feature: run AI face analysis on any public profile picture. Returns estimated age, gender, race/ethnicity, and dominant emotion. Ideal for audience research, demographic enrichment, and ML pipelines. Returns {"face_detected": false} when no face is present.',
                    code: `<span class="text-muted"># AI analysis of profile picture</span>
curl <span class="text-warning">"${base}/v1/user/face?id=25025320"</span> \\
  -H <span class="text-warning">"x-access-key: YOUR_ACCESS_KEY"</span>

<span class="text-muted"># Response</span>
{ <span class="text-primary">"face_detected"</span>: true,
  <span class="text-primary">"age"</span>: 28,
  <span class="text-primary">"gender"</span>: <span class="text-warning">"female"</span>,
  <span class="text-primary">"race"</span>: <span class="text-warning">"white"</span>,
  <span class="text-primary">"emotion"</span>: <span class="text-warning">"happy"</span> }`
                },
                {
                    path: '/v1/media/by/code',
                    title: 'Media by shortcode',
                    desc: 'Retrieve any Instagram post by its URL shortcode (the alphanumeric string in instagram.com/p/{CODE}/). Get full media data — caption, likes, comments count, media URLs, and carousel children.',
                    code: `<span class="text-muted"># Get post by URL shortcode</span>
curl <span class="text-warning">"${base}/v1/media/by/code?code=CxYz123ABC"</span> \\
  -H <span class="text-warning">"x-access-key: YOUR_ACCESS_KEY"</span>

<span class="text-muted"># Response</span>
{ <span class="text-primary">"pk"</span>: <span class="text-warning">"3216789..."</span>,
  <span class="text-primary">"code"</span>: <span class="text-warning">"CxYz123ABC"</span>,
  <span class="text-primary">"caption_text"</span>: <span class="text-warning">"Sunset 🌅"</span>,
  <span class="text-primary">"like_count"</span>: 45231,
  <span class="text-primary">"comment_count"</span>: 892,
  <span class="text-primary">"video_url"</span>: <span class="text-warning">"https://..."</span> }`
                },
                {
                    path: '/v1/hashtag/by/name',
                    title: 'Hashtag lookup',
                    desc: 'Get hashtag metadata by name — PK, total media count, and profile picture URL. The # prefix is optional. Perfect for trend discovery, content strategy, and competitive analysis.',
                    code: `<span class="text-muted"># Lookup hashtag by name</span>
curl <span class="text-warning">"${base}/v1/hashtag/by/name?name=travel"</span> \\
  -H <span class="text-warning">"x-access-key: YOUR_ACCESS_KEY"</span>

<span class="text-muted"># Response</span>
{ <span class="text-primary">"pk"</span>: <span class="text-warning">"17841562802328012"</span>,
  <span class="text-primary">"name"</span>: <span class="text-warning">"travel"</span>,
  <span class="text-primary">"media_count"</span>: 721500000,
  <span class="text-primary">"profile_pic_url"</span>: <span class="text-warning">"https://..."</span> }`
                },
            ]
        },
        products() {
            return [
                {
                    emoji: '⚡',
                    title: 'Cache API',
                    link: '/cache-api',
                    tagline: 'Fast REST API for real-time access to cached Instagram and TikTok data.',
                    features: [
                        'Sub-second response times',
                        'Profiles, posts, stories, hashtags',
                        'No rate limits, no blocks',
                        'Updated_at timestamps on every record',
                    ],
                },
                {
                    emoji: '📦',
                    title: 'Datasets',
                    link: '/datasets',
                    tagline: 'Ready-made bulk downloads of Instagram and TikTok data.',
                    features: [
                        'Users, posts, comments, hashtags',
                        'Filter by location or keyword',
                        'Build custom datasets on demand',
                        'CSV & JSON export',
                    ],
                },
                {
                    emoji: '🧠',
                    title: 'MCP Server',
                    link: '/mcp',
                    tagline: 'Model Context Protocol integration for Claude and other AI assistants.',
                    features: [
                        'Plug into Claude Desktop',
                        'Query social data from your AI',
                        'Profile lookups & analytics',
                        'Zero-config setup',
                    ],
                },
            ]
        },
        reasons() {
            return [
                {
                    icon: 'fe-shield', title: 'No blocks or bans',
                    body: 'Forget about captchas, IP blocks, and login errors. We manage the account pools, proxies, and session rotation — your requests always go through.',
                },
                {
                    icon: 'fe-zap', title: 'Built for speed',
                    body: 'Cache API returns results in under a second. Datasets are pre-aggregated and ready to download. No waiting for scraper jobs.',
                },
                {
                    icon: 'fe-trending-up', title: 'Scales with you',
                    body: 'From a hundred requests to millions per month. No rate limits on your side, your balance never expires.',
                },
                {
                    icon: 'fe-cpu', title: 'AI-native',
                    body: 'Our MCP Server plugs social data directly into <a href="https://claude.ai">Claude</a> and other AI assistants — no glue code required. Plus exclusive <strong>AI face analysis</strong> on profile pictures (age, gender, emotion).',
                },
                {
                    icon: 'fe-dollar-sign', title: 'Transparent pricing',
                    body: 'Pay only for successful responses. Server errors are free. From $0.0003 per request. <router-link to="/pricing">See pricing.</router-link>',
                },
                {
                    icon: 'fe-users', title: 'Trusted by teams',
                    body: 'Data scientists, marketing teams, and SaaS builders rely on ' + this.$state.siteName + ' for production workloads.',
                },
            ]
        },
        categories() {
            return [
                {
                    icon: '👤', title: 'Users',
                    features: ['Profile by username or ID', 'Extended "about" metadata', 'Country & registration date', 'Former usernames history'],
                },
                {
                    icon: '📸', title: 'Media',
                    features: ['Post by shortcode, ID, or URL', 'Caption, engagement metrics', 'Media & download URLs', 'Carousel children'],
                },
                {
                    icon: '📖', title: 'Stories & Highlights',
                    features: ['Story by ID or URL', 'Highlight by ID or URL', 'Media URLs & metadata'],
                },
                {
                    icon: '💬', title: 'Comments',
                    features: ['Comment lookup by ID', 'Text, author, timestamp'],
                },
                {
                    icon: '#️⃣', title: 'Hashtags',
                    features: ['Lookup by ID or name', 'Media count & profile pic', 'Name normalization'],
                },
                {
                    icon: '📍', title: 'Locations',
                    features: ['Lookup by ID or name', 'Address & coordinates', 'Location metadata'],
                },
                {
                    icon: '🎵', title: 'Audio tracks',
                    features: ['Track metadata by ID', 'Title, artist, duration'],
                },
                {
                    icon: '🧬', title: 'AI Face Analysis',
                    features: ['Estimated age & gender', 'Race/ethnicity detection', 'Dominant emotion', 'ML on profile pictures'],
                },
            ]
        },
        useCases() {
            return [
                { emoji: '📊', title: 'Analytics dashboards', body: 'Track follower counts, engagement rates, and posting patterns for any public Instagram account over time.' },
                { emoji: '🔎', title: 'Influencer enrichment', body: 'Enrich creator profiles with follower count, verification, country, and AI-detected face attributes for lead scoring.' },
                { emoji: '🧪', title: 'Data science', body: 'Run research on social trends, hashtag velocity, and demographic patterns with ready-made datasets.' },
                { emoji: '🤖', title: 'AI agents', body: 'Give Claude and other AI assistants live access to social data through our MCP Server — no custom glue code.' },
            ]
        },
    },
}
</script>

<style lang="scss" scoped>
@import '../scss/theme/variables';

.products-section {
    background: rgba($graphite-light, 0.3);
    border-top: 1px solid $graphite-light;
    border-bottom: 1px solid $graphite-light;
}

.data-section {
    background: rgba($graphite-light, 0.3);
    border-top: 1px solid $graphite-light;
    border-bottom: 1px solid $graphite-light;
}

.product-card,
.data-card {
    background: $graphite;
    transition: box-shadow .2s ease, transform .2s ease;
}

.product-card:hover {
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
}

.card-hover:hover {
    transform: translateY(-4px);
}

.code-card {
    background: #0b0d10;
    border: 1px solid $graphite-light;
}

.code-examples-section {
    background: rgba($graphite-light, 0.3);
    border-top: 1px solid $graphite-light;
    border-bottom: 1px solid $graphite-light;
}
</style>
